import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { TextField, Box, DatePicker as PolarisDatePicker, Popover, Icon, Card } from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';
import { formatDate } from '#client/utils/helpers';

// Create a forwarded ref wrapper component
const CardWithRef = forwardRef((props, ref) => (
    <div ref={ref}>
        <Card {...props} />
    </div>
));

CardWithRef.displayName = 'CardWithRef';

const DatePicker = ({ label, value, onChange, error, disableDatesBefore, disableDatesAfter }) => {
    const [visible, setVisible] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(value.getMonth());
    const [currentYear, setCurrentYear] = useState(value.getFullYear());
    const [inputValue, setInputValue] = useState(formatDate(value));
    const datePickerRef = useRef(null);

    // Update local state when value prop changes
    useEffect(() => {
        setCurrentMonth(value.getMonth());
        setCurrentYear(value.getFullYear());
        setInputValue(formatDate(value));
    }, [value]);

    const handleOnClose = useCallback(() => {
        setVisible(false);
    }, [setVisible]);

    const handleDateSelection = useCallback(
        ({ end: newDate }) => {
            onChange(newDate);
            setInputValue(formatDate(newDate));
            setVisible(false);
        },
        [onChange]
    );

    const handleFocus = useCallback(() => {
        setVisible(true);
    }, [setVisible]);

    const handleInputChange = useCallback(
        (newValue) => {
            setInputValue(newValue);

            // Try to parse the date and update if valid
            const parsedDate = new Date(newValue);
            if (!isNaN(parsedDate.getTime())) {
                onChange(parsedDate);
            }
        },
        [onChange]
    );

    const handleMonthChange = useCallback(
        (month, year) => {
            // Update local state immediately
            setCurrentMonth(month);
            setCurrentYear(year);
        },
        [setCurrentMonth, setCurrentYear]
    );

    const activator = (
        <TextField role="combobox" label={label} prefix={<Icon source={CalendarIcon} />} value={inputValue} onFocus={handleFocus} onChange={handleInputChange} autoComplete="off" error={error} />
    );

    return (
        <Box minWidth="276px">
            <Popover active={visible} activator={activator} autofocusTarget="first-node" onClose={handleOnClose}>
                <CardWithRef ref={datePickerRef}>
                    {(() => {
                        return (
                            <PolarisDatePicker
                                month={currentMonth}
                                year={currentYear}
                                onChange={handleDateSelection}
                                onMonthChange={handleMonthChange}
                                selected={value}
                                disableDatesBefore={disableDatesBefore}
                                disableDatesAfter={disableDatesAfter}
                                error={error}
                            />
                        );
                    })()}
                </CardWithRef>
            </Popover>
        </Box>
    );
};

export default DatePicker;
